import type { WeatherStation, WeatherAlert } from '@/shared/types'

interface Seed {
  code: string
  name: string
  type: WeatherStation['type']
  lat: number
  lon: number
  elev: number
  region: WeatherStation['region']
}

const SEEDS: Seed[] = [
  // North
  { code: 'WS-HAN', name: 'Trạm Hà Nội (Láng)', type: 'surface', lat: 21.02, lon: 105.81, elev: 5, region: 'north' },
  { code: 'WS-HAI', name: 'Trạm Hải Phòng', type: 'surface', lat: 20.85, lon: 106.68, elev: 5, region: 'north' },
  { code: 'WS-DBN', name: 'Trạm Điện Biên', type: 'surface', lat: 21.38, lon: 103.02, elev: 480, region: 'north' },
  { code: 'WS-LAC', name: 'Trạm Lào Cai', type: 'surface', lat: 22.40, lon: 103.96, elev: 90, region: 'north' },
  { code: 'WS-VIN', name: 'Trạm Vinh', type: 'surface', lat: 18.67, lon: 105.68, elev: 6, region: 'north' },

  // Central
  { code: 'WS-DAN', name: 'Trạm Đà Nẵng', type: 'surface', lat: 16.05, lon: 108.20, elev: 5, region: 'central' },
  { code: 'WS-HUE', name: 'Trạm Huế', type: 'surface', lat: 16.46, lon: 107.59, elev: 11, region: 'central' },
  { code: 'WS-PLE', name: 'Trạm Pleiku', type: 'surface', lat: 13.99, lon: 108.00, elev: 800, region: 'central' },
  { code: 'WS-DAL', name: 'Trạm Đà Lạt', type: 'surface', lat: 11.95, lon: 108.45, elev: 1500, region: 'central' },
  { code: 'WS-NTR', name: 'Trạm Nha Trang', type: 'surface', lat: 12.25, lon: 109.18, elev: 5, region: 'central' },

  // South
  { code: 'WS-SGN', name: 'Trạm TP.HCM (Tân Sơn Nhất)', type: 'surface', lat: 10.82, lon: 106.65, elev: 6, region: 'south' },
  { code: 'WS-CTH', name: 'Trạm Cần Thơ', type: 'surface', lat: 10.03, lon: 105.78, elev: 1, region: 'south' },
  { code: 'WS-CMU', name: 'Trạm Cà Mau', type: 'surface', lat: 9.18, lon: 105.15, elev: 1, region: 'south' },
  { code: 'WS-VTU', name: 'Trạm Vũng Tàu', type: 'surface', lat: 10.34, lon: 107.08, elev: 4, region: 'south' },
  { code: 'WS-PQU', name: 'Trạm Phú Quốc', type: 'surface', lat: 10.22, lon: 103.97, elev: 3, region: 'south' },

  // Upper-air sondes
  { code: 'UA-HAN', name: 'Sonde Hà Nội', type: 'upper-air', lat: 21.02, lon: 105.81, elev: 5, region: 'north' },
  { code: 'UA-DAN', name: 'Sonde Đà Nẵng', type: 'upper-air', lat: 16.05, lon: 108.20, elev: 5, region: 'central' },
  { code: 'UA-SGN', name: 'Sonde TP.HCM', type: 'upper-air', lat: 10.82, lon: 106.65, elev: 6, region: 'south' },

  // Marine
  { code: 'MA-HLO', name: 'Trạm Hòn Dấu', type: 'marine', lat: 20.67, lon: 106.81, elev: 1, region: 'sea' },
  { code: 'MA-CSN', name: 'Trạm Côn Sơn', type: 'marine', lat: 8.69, lon: 106.60, elev: 1, region: 'sea' },
  { code: 'MA-SPA', name: 'Trạm Trường Sa', type: 'marine', lat: 8.65, lon: 111.92, elev: 1, region: 'sea' },
  { code: 'MA-DSA', name: 'Trạm DK1 Bãi Tư Chính', type: 'marine', lat: 7.77, lon: 109.50, elev: 1, region: 'sea' },

  // Weather radar
  { code: 'RD-NB', name: 'Radar khí tượng Nội Bài', type: 'radar', lat: 21.22, lon: 105.81, elev: 50, region: 'north' },
  { code: 'RD-VN', name: 'Radar khí tượng Vinh', type: 'radar', lat: 18.74, lon: 105.67, elev: 8, region: 'north' },
  { code: 'RD-DG', name: 'Radar khí tượng Đông Hà', type: 'radar', lat: 16.81, lon: 107.10, elev: 8, region: 'central' },
  { code: 'RD-NT', name: 'Radar khí tượng Nha Trang', type: 'radar', lat: 12.23, lon: 109.20, elev: 5, region: 'central' },
  { code: 'RD-NB2', name: 'Radar khí tượng Nhà Bè', type: 'radar', lat: 10.70, lon: 106.74, elev: 5, region: 'south' },
]

function rand(seed: number, min: number, max: number): number {
  const x = Math.sin(seed) * 10000
  return min + (x - Math.floor(x)) * (max - min)
}

export const WEATHER_STATIONS: WeatherStation[] = SEEDS.map((s, idx) => {
  const tempBase = s.region === 'north' ? 22 : s.region === 'central' ? 27 : s.region === 'south' ? 30 : 28
  return {
    id: `wst-${idx + 1}`,
    code: s.code,
    name: s.name,
    type: s.type,
    lat: s.lat,
    lon: s.lon,
    elevation: s.elev,
    region: s.region,
    status: idx === 7 ? 'maintenance' : idx === 12 ? 'offline' : 'online',
    current: {
      temperature: Math.round(tempBase + rand(idx, -5, 8)),
      humidity: Math.round(rand(idx * 3, 55, 90)),
      pressure: Math.round(1010 + rand(idx * 5, -8, 8)),
      windSpeed: Math.round(rand(idx * 7, 5, 30)),
      windDirection: Math.round(rand(idx * 11, 0, 360)),
      visibility: Math.round(rand(idx * 13, 5, 20)),
      precipitation: rand(idx * 17, 0, 12),
    },
    lastUpdate: new Date(Date.now() - rand(idx * 19, 0, 1800) * 1000).toISOString(),
  }
})

export const WEATHER_ALERTS: WeatherAlert[] = [
  {
    id: 'wa-1',
    code: 'WA-2026-0512',
    type: 'thunderstorm',
    severity: 'warning',
    region: 'Bắc Trung Bộ',
    message: 'Vùng có khả năng dông sét, tầm nhìn giảm dưới 5km, gió giật mạnh cấp 7-8.',
    startsAt: new Date(Date.now() + 2 * 3600000).toISOString(),
    endsAt: new Date(Date.now() + 8 * 3600000).toISOString(),
    affectedAirports: ['ap-9', 'ap-6'],
  },
  {
    id: 'wa-2',
    code: 'WA-2026-0513',
    type: 'low-visibility',
    severity: 'critical',
    region: 'Đồng bằng Bắc Bộ',
    message: 'Sương mù dày đặc tại sân bay Nội Bài và Cát Bi. Tầm nhìn giảm còn 800m.',
    startsAt: new Date(Date.now() - 1800000).toISOString(),
    endsAt: new Date(Date.now() + 3 * 3600000).toISOString(),
    affectedAirports: ['ap-1', 'ap-4'],
  },
  {
    id: 'wa-3',
    code: 'WA-2026-0514',
    type: 'turbulence',
    severity: 'warning',
    region: 'Cao nguyên Trung Bộ',
    message: 'Nhiễu động không khí mức trung bình ở độ cao 8000-12000m.',
    startsAt: new Date(Date.now() + 1 * 3600000).toISOString(),
    endsAt: new Date(Date.now() + 6 * 3600000).toISOString(),
    affectedAirports: ['ap-8'],
  },
  {
    id: 'wa-4',
    code: 'WA-2026-0515',
    type: 'storm',
    severity: 'critical',
    region: 'Biển Đông',
    message: 'Áp thấp nhiệt đới đang mạnh lên thành bão số 3, di chuyển theo hướng Tây-Tây Bắc.',
    startsAt: new Date(Date.now() + 12 * 3600000).toISOString(),
    endsAt: new Date(Date.now() + 72 * 3600000).toISOString(),
    affectedAirports: ['ap-11', 'ap-10'],
  },
  {
    id: 'wa-5',
    code: 'WA-2026-0516',
    type: 'wind-shear',
    severity: 'warning',
    region: 'Sân bay Tân Sơn Nhất',
    message: 'Phát hiện wind shear ở độ cao thấp, ảnh hưởng đến cất hạ cánh.',
    startsAt: new Date(Date.now() - 600000).toISOString(),
    endsAt: new Date(Date.now() + 2 * 3600000).toISOString(),
    affectedAirports: ['ap-12'],
  },
  {
    id: 'wa-6',
    code: 'WA-2026-0517',
    type: 'icing',
    severity: 'info',
    region: 'Tây Bắc',
    message: 'Khả năng đóng băng nhẹ ở độ cao 5000-7000m phía Tây Bắc.',
    startsAt: new Date(Date.now() + 4 * 3600000).toISOString(),
    endsAt: new Date(Date.now() + 12 * 3600000).toISOString(),
    affectedAirports: ['ap-2'],
  },
]
